import { Controller } from "@hotwired/stimulus"

// Drives an "always-editable" inline form (e.g. a custom part or todo on the
// figure edit page) that mimics its display layout but with live inputs.
//
// Two operating modes, selected via `data-inline-editor-autosave-value`:
//
//   * Manual save (default): snapshot on connect; toggle Save/Cancel/Delete
//     based on dirty state; rely on user to click Save.
//
//   * Auto-save (autosave: true): hide Save/Cancel; debounce text input
//     changes and submit immediately on select/checkbox/file changes;
//     show a small "Saving…" / "Saved" status; resnapshot on success.
//     The matching server controllers detect a hidden `autosave=1` field
//     and respond with head :no_content, leaving the live form alone so
//     the user can keep editing without losing focus or pending edits.
//
// Markup contract:
//
//   <div data-controller="inline-editor"
//        data-inline-editor-autosave-value="true">
//     <form data-inline-editor-target="form">
//       ...inputs / selects / textareas...
//       <button type="submit" data-inline-editor-target="saveButton">Save</button>
//       <button type="button" data-inline-editor-target="cancelButton"
//               data-action="click->inline-editor#cancel">Cancel</button>
//       <span data-inline-editor-target="status"></span>  <!-- optional -->
//     </form>
//
//     <!-- Delete sits OUTSIDE the form so it doesn't share its action. -->
//     <div data-inline-editor-target="deleteButton">
//       <%= button_to "Delete", ..., method: :delete %>
//     </div>
//   </div>
const TEXT_INPUT_TYPES = new Set([
  "text", "search", "url", "email", "tel", "number", "password", "textarea"
])
const DEBOUNCE_MS = 800

export default class extends Controller {
  static targets = ["form", "saveButton", "cancelButton", "deleteButton", "status"]
  static values = {
    autosave: { type: Boolean, default: false }
  }

  connect() {
    this._inflight = false
    this._pendingResubmit = false
    this._debounceTimer = null

    // Forms containing a Trix editor need their initial snapshot
    // deferred until Trix has finished initializing, because Trix
    // re-serializes the rich-text body on init (image attachments
    // get canonical IDs, lists are normalized, etc.). If we
    // snapshot before that, the first trix-change after init looks
    // dirty and we fire a spurious autosave on page load. Capture
    // the snapshot inside trix-initialize so the baseline is the
    // canonical form of the saved content.
    const trix = this.element.querySelector("trix-editor")
    if (trix && !trix.editor) {
      this._pendingSnapshot = true
      this._onTrixInitialize = () => {
        this._pendingSnapshot = false
        this._snapshot = this._captureFormState()
        this._update()
      }
      trix.addEventListener("trix-initialize", this._onTrixInitialize, { once: true })
      // Provisional snapshot so _isDirty() doesn't NPE if a change
      // event sneaks in before trix-initialize. It'll get replaced
      // the moment Trix initializes.
      this._snapshot = this._captureFormState()
    } else {
      this._snapshot = this._captureFormState()
    }
    this._update()

    if (this.autosaveValue) {
      this._ensureAutosaveFlagInput()
      // Manual Save/Cancel are redundant in auto-save mode; remove them
      // entirely so the layout collapses cleanly. Delete stays.
      if (this.hasSaveButtonTarget) this.saveButtonTarget.remove()
      if (this.hasCancelButtonTarget) this.cancelButtonTarget.remove()

      this._onInput = (event) => this._handleInput(event)
      this._onChange = (event) => this._handleChange(event)
      this._onTrixChange = () => this._handleTrixChange()
      this._onSubmitStart = (event) => this._handleSubmitStart(event)
      this._onSubmitEnd = (event) => this._handleSubmitEnd(event)

      this.element.addEventListener("input", this._onInput)
      this.element.addEventListener("change", this._onChange)
      // Trix dispatches a non-bubbling-friendly custom event whenever
      // the user edits rich text. The hidden input that backs the
      // editor is type=hidden, so it never appears in our generic
      // input/change handlers above -- listen for the Trix-specific
      // event explicitly. It bubbles so attaching to the controller
      // root catches edits regardless of where the editor sits.
      this.element.addEventListener("trix-change", this._onTrixChange)
      this.formTarget.addEventListener("turbo:submit-start", this._onSubmitStart)
      this.formTarget.addEventListener("turbo:submit-end", this._onSubmitEnd)
    } else {
      this._onChange = () => this._update()
      // Listen on the controller root rather than the <form> itself so we
      // catch input/change events from fields associated to the form via
      // the HTML5 `form` attribute (e.g. selects rendered alongside the
      // action buttons in the second row of the todo inline editor).
      this.element.addEventListener("input", this._onChange)
      this.element.addEventListener("change", this._onChange)
    }
  }

  disconnect() {
    if (this._debounceTimer) clearTimeout(this._debounceTimer)
    if (this._onInput) this.element.removeEventListener("input", this._onInput)
    if (this._onTrixChange) this.element.removeEventListener("trix-change", this._onTrixChange)
    if (this._onTrixInitialize) {
      const trix = this.element.querySelector("trix-editor")
      if (trix) trix.removeEventListener("trix-initialize", this._onTrixInitialize)
    }
    if (this._onChange) {
      this.element.removeEventListener("input", this._onChange)
      this.element.removeEventListener("change", this._onChange)
    }
    if (this.hasFormTarget) {
      if (this._onSubmitStart) this.formTarget.removeEventListener("turbo:submit-start", this._onSubmitStart)
      if (this._onSubmitEnd) this.formTarget.removeEventListener("turbo:submit-end", this._onSubmitEnd)
    }
  }

  cancel(event) {
    if (event) event.preventDefault()
    // The browser's native form.reset() restores defaultValue / defaultChecked
    // on every form element AND fires a `reset` event that other controllers
    // (e.g. image-uploader) listen for to clear their own UI state. That's
    // simpler and more correct than poking individual values back manually.
    this.formTarget.reset()
    this._update()
  }

  // Explicit, non-autosave submission. Used by escape hatches inside an
  // otherwise-autosave form -- e.g. the figure form's "Save & publish"
  // button in the inline username panel, which needs the server to run
  // its full update path (including the username-required redirect)
  // instead of head :no_content. Clears the autosave flag so the server
  // treats this submission like an old-school button click.
  manualSubmit(event) {
    if (event) event.preventDefault()
    if (this._debounceTimer) {
      clearTimeout(this._debounceTimer)
      this._debounceTimer = null
    }
    this._setAutosaveFlag(false)
    this.formTarget.requestSubmit()
  }

  // ---- auto-save plumbing ------------------------------------------------

  _handleInput(event) {
    const el = event.target
    if (!this._isFormField(el)) return
    const type = (el.tagName === "TEXTAREA") ? "textarea" : el.type
    // Non-text fields are handled in `_handleChange` for immediate save;
    // skip them here so we don't double-fire.
    if (!TEXT_INPUT_TYPES.has(type)) return
    // A field searched by typing (e.g. the custom's Property picker) saves
    // only when it's left or a choice is picked, never a half-typed word.
    if (el.dataset.autosaveOn === "change") return
    this._scheduleAutosave()
  }

  _handleChange(event) {
    const el = event.target
    if (!this._isFormField(el)) return
    const type = (el.tagName === "TEXTAREA") ? "textarea" : el.type
    // Files / selects / checkboxes / radios should commit immediately;
    // text fields already debounced via the `input` listener so a final
    // `change` (blur) just flushes any pending debounce.
    if (el.dataset.autosaveOn === "change") {
      this._submitNow()
    } else if (TEXT_INPUT_TYPES.has(type)) {
      this._flushAutosave()
    } else {
      this._submitNow()
    }
  }

  _handleTrixChange() {
    // Ignore trix-change events that fire before the initial Trix
    // canonicalization snapshot lands -- those are init noise, not
    // user edits.
    if (this._pendingSnapshot) return
    this._scheduleAutosave()
  }

  _scheduleAutosave() {
    if (this._debounceTimer) clearTimeout(this._debounceTimer)
    this._setStatus("dirty")
    this._debounceTimer = setTimeout(() => this._submitNow(), DEBOUNCE_MS)
  }

  _flushAutosave() {
    if (this._debounceTimer) {
      clearTimeout(this._debounceTimer)
      this._debounceTimer = null
      if (this._isDirty()) this._submitNow()
    }
  }

  _submitNow() {
    if (this._debounceTimer) {
      clearTimeout(this._debounceTimer)
      this._debounceTimer = null
    }
    // Nothing changed after all (e.g. Trix re-saying its own content):
    // take back the "Editing…" the change put up.
    if (!this._isDirty()) return this._setStatus(this._saved ? "saved" : "idle")
    if (this._inflight) {
      // Coalesce: when the in-flight save returns we'll re-check dirty
      // state and submit again to capture whatever changed since.
      this._pendingResubmit = true
      return
    }
    // Skip the autosave fast-path when there are pending file uploads --
    // the server side wants to render thumbnails for newly-attached
    // images, which only happens in the full turbo_stream response.
    const hasPendingFiles = this._hasPendingFiles()
    this._setAutosaveFlag(!hasPendingFiles)
    this.formTarget.requestSubmit()
  }

  _handleSubmitStart() {
    this._inflight = true
    // Snapshot the values that are actually being sent so we can
    // re-base dirty state against THOSE on submit-end -- not against
    // any keystrokes the user makes while the request is in flight.
    this._inflightSnapshot = this._captureFormState()
    this._setStatus("saving")
  }

  _handleSubmitEnd(event) {
    this._inflight = false
    const success = !!event.detail?.success
    if (success) {
      // Reset file inputs after upload so they aren't resubmitted on the
      // next autosave (the server already attached the bytes). We only
      // get here when no files were pending (file submits go through
      // the full turbo_stream replace path), so this is just defensive.
      this._clearFileInputs()
      // Re-base dirty state to what was actually saved, not to "now".
      // Any keystrokes that happened during the request are still in
      // the live inputs; if they differ from the saved snapshot we
      // schedule another save so the user's latest edits aren't
      // silently dropped.
      this._snapshot = this._inflightSnapshot || this._captureFormState()
      this._inflightSnapshot = null
      if (this._isDirty()) {
        this._setStatus("dirty")
        this._scheduleAutosave()
      } else {
        this._saved = true
        this._setStatus("saved")
      }
    } else {
      this._inflightSnapshot = null
      this._setStatus("error")
    }
    if (this._pendingResubmit) {
      this._pendingResubmit = false
      if (this._isDirty()) this._submitNow()
    }
  }

  _isFormField(el) {
    if (!el || !el.tagName) return false
    if (!["INPUT", "TEXTAREA", "SELECT"].includes(el.tagName)) return false
    if (el.disabled) return false
    if (el.name === "autosave") return false
    return true
  }

  _hasPendingFiles() {
    return Array.from(this.formTarget.elements).some(
      (el) => el.type === "file" && el.files && el.files.length > 0
    )
  }

  _clearFileInputs() {
    Array.from(this.formTarget.elements).forEach((el) => {
      if (el.type === "file" && el.files && el.files.length > 0) {
        el.value = ""
      }
    })
  }

  // Autosave-only: flip the hidden marker the server checks so it knows
  // to respond with head :no_content instead of replacing the frame.
  _ensureAutosaveFlagInput() {
    let input = this.formTarget.querySelector('input[name="autosave"][data-inline-editor-flag="true"]')
    if (!input) {
      input = document.createElement("input")
      input.type = "hidden"
      input.name = "autosave"
      input.dataset.inlineEditorFlag = "true"
      this.formTarget.appendChild(input)
    }
    this._autosaveInput = input
  }

  _setAutosaveFlag(on) {
    if (!this._autosaveInput) return
    this._autosaveInput.value = on ? "1" : ""
  }

  _setStatus(state) {
    if (!this.hasStatusTarget) return
    const map = {
      idle:   { text: "",          cls: "text-gray-500" },
      dirty:  { text: "Editing…",  cls: "text-gray-400" },
      saving: { text: "Saving…",   cls: "text-blue-300" },
      saved:  { text: "Saved",     cls: "text-green-400" },
      error:  { text: "Save failed", cls: "text-red-400" }
    }
    const entry = map[state] || map.idle
    this.statusTarget.textContent = entry.text
    this.statusTarget.className = `text-[10px] uppercase tracking-wider transition ${entry.cls}`
  }

  // ---- shared dirty-state plumbing --------------------------------------

  _captureFormState() {
    return Array.from(this.formTarget.elements).map(el => ({
      el,
      type: el.type,
      value: el.value,
      checked: el.checked
    }))
  }

  _isDirty() {
    return this._snapshot.some(({ el, type, value, checked }) => {
      if (!el.isConnected) return false
      if (type === "checkbox" || type === "radio") return el.checked !== checked
      if (type === "file") return el.files && el.files.length > 0
      return el.value !== value
    })
  }

  _update() {
    if (this.autosaveValue) return
    const dirty = this._isDirty()
    if (this.hasSaveButtonTarget) this.saveButtonTarget.disabled = !dirty
    if (this.hasCancelButtonTarget) this.cancelButtonTarget.classList.toggle("hidden", !dirty)
    if (this.hasDeleteButtonTarget) this.deleteButtonTarget.classList.toggle("hidden", dirty)
    this.element.classList.toggle("is-dirty", dirty)
  }
}
